import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { useSite } from "../../context/siteContext";
import PageTitle from "../PageTitle/PageTitle";
import { ASSETS_BASE_URL } from "../../config";
import { getProductMachines } from "../../services/pdfProductMachineFinderService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";

const defaultImageSrc = "/MachineImages/default.png"; // fallback image

const PDFProductLinkMachineFinder = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { product_id } = useParams();
	const { selectedSite } = useSite();

	const machineId = localStorage.getItem("last_clicked_machine_id");
	const [machines, setMachines] = useState([]);
	const [loading, setLoading] = useState(true);
	const [machineImages, setMachineImages] = useState({});
	const [noMachinesAvailable, setNoMachinesAvailable] = useState(false);
	const default_img = `${ASSETS_BASE_URL}/stock.png`;
	const [validatingMachine, setValidatingMachine] = useState(false);

	useEffect(() => {
		const fetchMachineFromProduct = async () => {
			try {
				const reqBody = { product_id, selectedSite };
				const response = await getProductMachines(reqBody);
				const machineList = response.data?.data || [];

				// Check if no machines are available
				if (machineList.length === 0) {
					setNoMachinesAvailable(true);
					setLoading(false);
					return;
				}

				// Case 1: validate localStorage machine without changing loading
				if (machineId) {
					setValidatingMachine(true);
					try {
						const checkRes = await getProductsByMachineId({
							machine_id: machineId,
							site_number: selectedSite,
						});
						const { products } = normalizeMachineProductsResponse(checkRes);
						const isValid = products.some((p) => p.product_id === parseInt(product_id, 10));

						if (isValid) {
							setValidatingMachine(false);
							navigate(`/machines/${machineId}/${product_id}`, { replace: true });
							return; // spinner stays until navigation
						}
					} catch (err) {
						setValidatingMachine(false);
						console.error("Error validating machine/product:", err);
					}
				}

				// Case 2: if only one machine has the product
				if (machineList.length === 1) {
					navigate(`/machines/${machineList[0].machine_id}/${product_id}`, { replace: true });
				} else {
					// Case 3: multiple possible machines → let user choose
					setMachines(machineList);
				}
			} catch (error) {
				console.error("API error:", error);
				// Set no machines available on API error as well
				setNoMachinesAvailable(true);
			} finally {
				setLoading(false); // only toggle once at the end
			}
		};

		fetchMachineFromProduct();
	}, [machineId, navigate, product_id, selectedSite]);

	// Load machine images
	useEffect(() => {
		const loadImages = async () => {
			const updated = {};
			for (const machine of machines) {
				const imgUrl = `${ASSETS_BASE_URL}/MachineImages/${machine.machine_number}.png`;
				try {
					const res = await fetch(imgUrl, { method: "HEAD" });
					updated[machine.machine_id] = res.ok ? imgUrl : default_img;
				} catch {
					updated[machine.machine_id] = default_img;
				}
			}
			setMachineImages(updated);
		};

		if (machines.length > 0) loadImages();
	}, [machines]);

	if (loading || validatingMachine) return <Box p={4}><CircularProgress /></Box>;

	// Handle no machines available case
	if (noMachinesAvailable) {
		return (
			<Box sx={{ width: "100%" }}>
				<PageTitle title={t('GENERAL.PRODUCT_NOT_AVAILABLE')} />
				<Box sx={{ width: { xs: "100%", md: "70%" }, marginTop: "20px", mx: "auto" }}>
					<Alert severity="warning" sx={{ mb: 2 }}>
						<Typography variant="h6" gutterBottom>
							{t('GENERAL.MACHINE_PRODUCT_NOT_AVAILABLE')}
						</Typography>
						<Typography variant="body1">
							{t('GENERAL.THIS_PRODUCT_IS_NOT_AVAILABLE_ON_ANY_MACHINES_AT_THE_SELECTED_SITE')}
						</Typography>
					</Alert>
					<Button
						variant="contained"
						onClick={() => navigate(-1)}
						sx={{ mt: 2 }}
					>
						{t('GENERAL.GO_BACK')}
					</Button>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ width: "100%" }}>
			<PageTitle title={t('GENERAL.SELECT_A_MACHINE')} />
			<Box sx={{ width: { xs: "100%", md: "70%" }, marginTop: "20px", mx: "auto" }}>
				{machines.map((machine) => (
					<Box
						key={machine.machine_id}
						display="flex"
						flexDirection={{ xs: "column", md: "row" }}
						justifyContent="space-between"
						alignItems="center"
						borderBottom="1px solid #ddd"
						padding="10px 0"
					>
						{/* Left: Machine Image */}
						<Box display="flex" alignItems="center" justifyContent="flex-start">
							<img
								src={machineImages[machine.machine_id] || default_img}
								alt={machine.machine_name}
								style={{ maxWidth: "100px", maxHeight: "100px", marginRight: "20px" }}
							/>
						</Box>

						{/* Middle: Machine Details */}
						<Box flex={1}>
							<Typography variant="h6">{machine.machine_name}</Typography>
							<Typography variant="body2" color="textSecondary">
								<b>{t('UI_TEXT.MACHINE')}:</b> {machine.machine_number}
							</Typography>
						</Box>

						{/* Right: Button */}
						<Box sx={{ mt: { xs: 1, md: 0 } }}>
							<Button
								variant="contained"
								onClick={() => navigate(`/machines/${machine.machine_id}/${product_id}`)}
							>
								{t('GENERAL.GO_TO_PRODUCT')}
							</Button>
						</Box>
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default PDFProductLinkMachineFinder;
